/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PluginManager } from '@opengeoweb/plugin-interface';
import type { DebugLogEntry, DebugLogEntryInput } from './pluginDebugTypes';

interface PluginDebugContextType {
  debugEntries: DebugLogEntry[];
  addDebugEntry: (entry: DebugLogEntryInput) => void;
  clearDebugEntries: () => void;
  /**
   * Registry of live {@link PluginManager}s, keyed by their owning
   * `instanceId` (the `sourceId` passed to `PluginComponent`). Populated by
   * {@link useRegisterDebugManager}; consumed by the debug inspector to
   * subscribe to `onDebugEvent` streams.
   */
  managers: Record<string, PluginManager>;
  registerManager: (instanceId: string, manager: PluginManager) => void;
  unregisterManager: (instanceId: string) => void;
}

const noop = (): void => {};

/**
 * Fallback value used when a consumer reads the debug context without a
 * surrounding {@link PluginDebugProvider}. Keeps the debug bridge hooks a
 * safe no-op outside of debug-enabled trees, so production code can call
 * `useRegisterDebugManager` unconditionally without pulling the debug UI in.
 */
const defaultPluginDebugContext: PluginDebugContextType = {
  debugEntries: [],
  addDebugEntry: noop,
  clearDebugEntries: noop,
  managers: {},
  registerManager: noop,
  unregisterManager: noop,
};

const PluginDebugContext = createContext<PluginDebugContextType>(
  defaultPluginDebugContext,
);

export const PluginDebugProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [debugEntries, setDebugEntries] = useState<DebugLogEntry[]>([]);
  const [managers, setManagers] = useState<Record<string, PluginManager>>({});
  const nextDebugIdRef = useRef(1);

  const addDebugEntry = useCallback((entry: DebugLogEntryInput) => {
    const newEntry = {
      ...entry,
      id: String(nextDebugIdRef.current),
      timestamp: new Date().toISOString(),
    } as DebugLogEntry;
    nextDebugIdRef.current += 1;
    setDebugEntries((prev) => [newEntry, ...prev]);
  }, []);

  const clearDebugEntries = useCallback(() => {
    setDebugEntries([]);
  }, []);

  const registerManager = useCallback(
    (instanceId: string, manager: PluginManager) => {
      setManagers((prev) => {
        if (prev[instanceId] === manager) {
          return prev;
        }
        return { ...prev, [instanceId]: manager };
      });
    },
    [],
  );

  const unregisterManager = useCallback((instanceId: string) => {
    setManagers((prev) => {
      if (!(instanceId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[instanceId];
      return next;
    });
  }, []);

  const value = useMemo<PluginDebugContextType>(
    () => ({
      debugEntries,
      addDebugEntry,
      clearDebugEntries,
      managers,
      registerManager,
      unregisterManager,
    }),
    [
      debugEntries,
      addDebugEntry,
      clearDebugEntries,
      managers,
      registerManager,
      unregisterManager,
    ],
  );

  return (
    <PluginDebugContext.Provider value={value}>
      {children}
    </PluginDebugContext.Provider>
  );
};

export const useDebugLog = (): PluginDebugContextType =>
  useContext(PluginDebugContext);

/**
 * Register a {@link PluginManager} with the debug layer so the inspector
 * can subscribe to its `onDebugEvent` stream.
 *
 * Safe to call from production code without a surrounding
 * {@link PluginDebugProvider}: the context falls back to no-op handlers, so
 * nothing is retained and no debug machinery is engaged.
 */
export const useRegisterDebugManager = (
  instanceId: string,
  manager: PluginManager | null,
): void => {
  const { registerManager, unregisterManager } = useDebugLog();

  useEffect(() => {
    if (!manager) {
      return undefined;
    }
    registerManager(instanceId, manager);
    return (): void => {
      unregisterManager(instanceId);
    };
  }, [instanceId, manager, registerManager, unregisterManager]);
};
